import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Stack, Button, Input } from '@/components/ui';
import type {
  Company,
  CompanyGroup,
  PoolOption,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyFormValues,
} from '@/types/companyManagement.types';

interface CompanyFormProps {
  company?: Company | null;
  pools: PoolOption[];
  groups: CompanyGroup[];
  onSubmit: (data: CreateCompanyRequest | UpdateCompanyRequest) => Promise<void>;
  onCancel: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Company name is required').min(1).max(255),
  startDate: Yup.string()
    .required('Start date is required')
    .test('start-before-end', 'Start date must not be after end date', function (startVal) {
      const { endDate } = this.parent;
      if (!startVal || !endDate) return true;
      return new Date(startVal) <= new Date(endDate);
    }),
  endDate: Yup.string()
    .nullable()
    .test('end-after-start', 'End date must not be before start date', function (endVal) {
      const { startDate } = this.parent;
      if (!endVal || !startDate) return true;
      return new Date(endVal) >= new Date(startDate);
    }),
});

function toDateInputValue(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
}

function toISOString(dateStr: string): string | null {
  if (!dateStr) return null;
  return new Date(dateStr).toISOString();
}

function CheckRow({
  id,
  checked,
  disabled,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-sm text-gray-800 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-dark-700"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-dark-600 dark:bg-dark-800"
      />
      <span className="min-w-0 leading-snug">{label}</span>
    </label>
  );
}

export const CompanyForm: React.FC<CompanyFormProps> = ({
  company,
  pools,
  groups,
  onSubmit,
  onCancel,
}) => {
  const getInitialValues = (): CompanyFormValues => ({
    name: company?.name ?? '',
    isInactive: company?.isInactive ?? false,
    startDate: toDateInputValue(company?.startDate) || toDateInputValue(new Date().toISOString()),
    endDate: toDateInputValue(company?.endDate),
    selectedPools: company?.poolIds.map((p) => p.poolId) ?? [],
    selectedGroups: company?.groups.map((g) => g.code) ?? [],
  });

  const handleFormSubmit = async (values: CompanyFormValues) => {
    const payload: CreateCompanyRequest | UpdateCompanyRequest = {
      name: values.name.trim(),
      isInactive: values.isInactive,
      startDate: values.startDate ? toISOString(values.startDate) : null,
      endDate: values.endDate ? toISOString(values.endDate) : null,
      poolIds: values.selectedPools,
      groups: values.selectedGroups.length > 0 ? values.selectedGroups : null,
    };
    await onSubmit(payload);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-dark-700 sm:px-5">
        <h2 id="company-form-title" className="type-dialog-title min-w-0">
          {company ? 'Edit company' : 'Add company'}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-dark-700 dark:hover:text-gray-200"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      </div>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, setFieldTouched, errors: formErrors, isSubmitting }) => (
          <Form className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4">
              <Stack spacing={3}>
                <Input
                  name="name"
                  label="Company name"
                  placeholder="Name"
                  disabled={isSubmitting}
                  required
                />

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
                  <Input
                    name="startDate"
                    type="date"
                    label="Start date"
                    disabled={isSubmitting}
                    required
                    max={values.endDate || undefined}
                  />
                  <Input
                    name="endDate"
                    type="date"
                    label="End date"
                    disabled={isSubmitting}
                    min={values.startDate || undefined}
                  />
                </div>

                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 dark:border-dark-600 dark:bg-dark-900/30">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={values.isInactive}
                    disabled={isSubmitting}
                    onClick={() => setFieldValue('isInactive', !values.isInactive)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-dark-800 ${
                      values.isInactive ? 'bg-red-500' : 'bg-gray-200 dark:bg-dark-600'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                        values.isInactive ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Inactive company
                    {values.isInactive && (
                      <span className="ml-1.5 text-xs font-normal text-red-600 dark:text-red-400">
                        (hidden from active use)
                      </span>
                    )}
                  </span>
                </div>

                <fieldset className="min-w-0">
                  <legend className="type-caption mb-2 block font-semibold text-gray-700 dark:text-gray-300">
                    Pools
                  </legend>
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 p-2 dark:border-dark-600 dark:bg-dark-900/40">
                    {pools.length === 0 ? (
                      <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No pools available
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                        {pools.map((pool) => (
                          <CheckRow
                            key={pool.poolId}
                            id={`pool-${pool.poolId}`}
                            checked={values.selectedPools.includes(pool.poolId)}
                            disabled={isSubmitting}
                            label={pool.pool}
                            onChange={(checked) => {
                              const updated = checked
                                ? [...values.selectedPools, pool.poolId]
                                : values.selectedPools.filter((id) => id !== pool.poolId);
                              setFieldValue('selectedPools', updated);
                              setFieldTouched('selectedPools', true, false);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </fieldset>

                <fieldset className="min-w-0">
                  <legend className="type-caption mb-2 block font-semibold text-gray-700 dark:text-gray-300">
                    Roles
                  </legend>
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 p-2 dark:border-dark-600 dark:bg-dark-900/40">
                    {groups.length === 0 ? (
                      <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No roles available
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                        {groups.map((group) => (
                          <CheckRow
                            key={group.code}
                            id={`group-${group.code}`}
                            checked={values.selectedGroups.includes(group.code)}
                            disabled={isSubmitting}
                            label={group.name}
                            onChange={(checked) => {
                              const updated = checked
                                ? [...values.selectedGroups, group.code]
                                : values.selectedGroups.filter((c) => c !== group.code);
                              setFieldValue('selectedGroups', updated);
                              setFieldTouched('selectedGroups', true, false);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {formErrors.selectedGroups && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                      {formErrors.selectedGroups as string}
                    </p>
                  )}
                </fieldset>
              </Stack>
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50/95 px-4 pb-8 pt-4 dark:border-dark-700 dark:bg-dark-900/60 sm:flex-row sm:items-center sm:justify-end sm:px-5 sm:pb-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
                leftIcon={
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
                leftIcon={
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                }
              >
                {company ? 'Save' : 'Create'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
